<template>
    <div>
        <h2 class="onboarding-title">Setup Your Organization</h2>
        <p class="onboarding-desc">Tell us who you are and how you'd like proofing to work by default.</p>

        <!-- Company Name Card -->
        <div class="onboarding-card">
            <div class="onboarding-card-section">
                <div class="form-group mb-0">
                    <label for="companyName" class="auth-label">Your Company Name</label>
                    <input v-model="companyName" type="text" id="companyName" class="auth-field" />
                </div>
            </div>
        </div>

        <!-- Proofing Defaults Card -->
        <div class="onboarding-card">
            <div class="card-head">
                <div class="card-head-title">Proofing Defaults</div>
                <div class="card-head-subtitle">Every new proof you create starts with these permissions. You can still adjust them per proof.</div>
            </div>
            <div class="toggle-rows">
                <div v-for="(setting, key) in proofingDefaults" :key="key" class="toggle-row">
                    <div class="ashore-toggle toggle-control">
                        <label class="switch">
                            <input type="checkbox" :id="key" v-model="setting.value" />
                            <span class="slider round"></span>
                        </label>
                    </div>
                    <div class="toggle-text">
                        <label :for="key" class="toggle-title">{{ setting.label }}</label>
                        <div class="toggle-description">{{ setting.tooltip }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Buttons -->
        <div class="onboarding-actions">
            <button @click="handleContinueClick" class="auth-btn-primary">Continue</button>
            <button @click="handlePrevStep" class="auth-btn-ghost">Previous</button>
        </div>
    </div>
</template>

<script>

import { ALERT_TYPE } from '../../utils/constants';
import { isWatermarkPlanEnabled } from '../../utils/helpers';

const { WARNING, SUCCESS } = ALERT_TYPE;

const defaultNotificationSettings = (userSettings) => ({
    notifyFilesSent: true,
    notifyFilesApproved: true,
    notifyNewComments: true,
    notifyJobAssigned: true,
    notifyMeNewComments: 1,
    notifyMyApproversNewComments: 1,
    reminderDays: "Sun,Mon,Tue,Wed,Tr,Fri,Sat",
    reminderTimeOfDay: "09:00",
});


export default {
    name: "Screen2",
    props: {
        currentStep: {
            type: Number,
            required: true
        },
        handleNextStep: {
            type: Function,
            required: true
        },
        handlePrevStep: {
            type: Function,
            required: true
        },
    },
    data() {
        const userSettings = _.get(this, '$A.Auth.userSettings', {});
        const onboardingStep = _.get(this, '$A.Auth.onboardingStep', 1);
        const hasCompletedStep2 = onboardingStep > 2;

        return {
            isSubmitting: false,
            companyName: _.get(this, '$A.Auth.account.name', ''),
            proofingDefaults: {
                viewAllComments: {
                    label: 'Enable real-time collaboration',
                    value: hasCompletedStep2 ? userSettings.viewAllComments : true,
                    tooltip: 'Turn this toggle on to allow your approvers and commenters to see, interact with, and be notified about other comments on a proof.'
                },
                blockCommentingAfterProofDecision: {
                    label: 'Disallow comments after an approver makes a decision',
                    value: hasCompletedStep2 ? userSettings.blockCommentingAfterProofDecision : true,
                    tooltip: 'Turn this toggle on to prevent approvers from submitting comments after making a decision.'
                },
                downloadFiles: {
                    label: 'Allow approvers to download files',
                    value: hasCompletedStep2 ? userSettings.downloadFiles : true,
                    tooltip: 'Turn this toggle on to allow approvers to download the files in your proof.'
                },
                approveIndividualFiles: {
                    label: 'Require approvers to approve each file individually',
                    value: hasCompletedStep2 ? userSettings.approveIndividualFiles : true,
                    tooltip: "Turn this toggle on to require approvers to make an approval decision on each file in your proof. Otherwise, we'll ask for one approval decision for all files in this proof."
                },
                approveWithChanges: {
                    label: 'Allow approvers to approve files with changes',
                    value: hasCompletedStep2 ? userSettings.approveWithChanges : true,
                    tooltip: "Turn this toggle on to allow your approvers to say, 'Make these changes, but I don't need to see a new version.' The proof will be treated as approved."
                },
                limitVersionVisibility: {
                    label: 'Hide internal versions of files from customers',
                    value: hasCompletedStep2 ? userSettings.limitVersionVisibility : true,
                    tooltip: 'Turn this toggle on to prevent approvers from viewing proof versions sent to approvers in other workflow stages.'
                },
                // Watermarking is only available on standard and premium plans.
                ...(isWatermarkPlanEnabled(this.$A.Auth)
                    ? {
                        watermarkEnabled: {
                            label: 'Watermark files with your account logo',
                            value: hasCompletedStep2 ? userSettings.watermarkEnabled : false,
                            tooltip: 'Turn this toggle on to stamp your account logo on every page of PDFs uploaded to your proofs.'
                        },
                    }
                    : {}),
            },
        };
    },
    mounted() {
        this.setCompanyName();
    },
    methods: {
        setCompanyName() {
            this.companyName = _.get(this, '$A.Auth.account.name', '');
        },
        async updateCompanyName() {
            if (!this.companyName || this.companyName.trim() === '') {
                this.$A.AlertUser("Company name cannot be empty", WARNING);
                return false;
            }

            try {
                const account = await this.$A.AccountService.UpdateAccount(
                    this.companyName,
                );

                if (account) {
                    this.$A.Auth.account = account;
                    this.$A.SaveSessionData(this.$A.Auth);
                    this.$A.AlertUser("Company name updated successfully", SUCCESS);
                    return true;
                }
                return false;
            } catch (error) {
                this.$A.AlertUser("Failed to update company name", WARNING);
                return false;
            }
        },

        async handleContinueClick() {
            if (this.isSubmitting) {
                return;
            }
            this.isSubmitting = true;
            try {
                const companyUpdated = await this.updateCompanyName();
                if (!companyUpdated) return;

                try {
                    const userSettings = _.get(this, '$A.Auth.userSettings', {});
                    const notificationSettings = defaultNotificationSettings(userSettings);
                    const user = await this.$A.UserService.UpdateUserDefaultSettings(
                        this.$A.Auth.id,
                        this.proofingDefaults.downloadFiles.value,
                        this.proofingDefaults.approveIndividualFiles.value,
                        this.proofingDefaults.viewAllComments.value,
                        this.proofingDefaults.approveWithChanges.value,
                        notificationSettings.notifyFilesSent,
                        notificationSettings.notifyFilesApproved,
                        notificationSettings.notifyNewComments,
                        notificationSettings.notifyMeNewComments,
                        notificationSettings.notifyMyApproversNewComments,
                        this.proofingDefaults.limitVersionVisibility.value,
                        this.proofingDefaults.blockCommentingAfterProofDecision.value,
                        notificationSettings.reminderDays,
                        notificationSettings.reminderTimeOfDay,
                        _.get(this, '$A.Auth.userSettings.reminderTimeZone', 'UTC'),
                        _.get(this, 'proofingDefaults.watermarkEnabled.value', !!userSettings.watermarkEnabled),
                        userSettings.dailyDigestTimeOfDay,
                        notificationSettings.notifyJobAssigned
                    );

                    if (user.id !== undefined && user.id > 0) {
                        this.$A.UpdateUserObjectAndBustCache(user);
                        this.handleNextStep();
                    } else {
                        this.$A.AlertUser("Failed to update proofing settings", WARNING);
                    }
                } catch (error) {
                    this.$A.AlertUser("Failed to update proofing settings", WARNING);
                }
            } finally {
                this.isSubmitting = false;
            }
        }
    }
};
</script>

<style scoped>
/* Matches the profile page's settings-card treatment (setting-profilePreferences.vue) */
.card-head {
    padding: 14px 20px;
    background: #fbfbfc;
    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
    border-radius: 11px 11px 0 0;
}

.card-head-title {
    font-size: 15px;
    font-weight: 600;
    color: #111827;
}

.card-head-subtitle {
    margin-top: 1px;
    font-size: 12.5px;
    font-weight: 400;
    color: #6b7280;
}

.toggle-rows {
    display: flex;
    flex-direction: column;
}

.toggle-row {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 15px 20px;
    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}

.toggle-row:last-child {
    border-bottom: 0;
}

.toggle-control {
    flex-shrink: 0;
    margin-top: 1px;
}

.toggle-control .switch {
    margin-right: 0;
}

.toggle-text {
    flex: 1;
    min-width: 0;
}

.toggle-title {
    font-size: 13.5px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0;
    cursor: pointer;
    display: block;
}

.toggle-description {
    margin-top: 1px;
    font-size: 12.5px;
    font-weight: 400;
    color: #6b7280;
    line-height: 1.5;
}

.ashore-toggle input:checked + .slider {
    background-color: #019256;
}
</style>
